import React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { useTranslation } from 'react-i18next';
import theme from '../theme';
import { Button } from './Button';

type Props = {
  open: boolean;
  title?: string;
  body?: string;
  handleClose: () => void;
};

export const NotificationModal: React.FC<Props> = ({
  open,
  title,
  body,
  handleClose,
}) => {
  const { t } = useTranslation(['main']);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      fullScreen={fullScreen}
      aria-labelledby='notification-modal-title'
      PaperProps={{
        style: {
          borderRadius: fullScreen ? 0 : '4px',
          overflow: 'hidden',
          // the red brand bar runs along the top edge of the modal
          borderTop: `4px solid ${theme.palette.primary.red}`,
        },
      }}
    >
      <DialogTitle
        // rendered as a div instead of the default h2, so the badge and the
        // close button are not nested inside a heading
        component='div'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(2),
          padding: theme.spacing(2.5, 3),
          backgroundColor: theme.custom.navigationBackground,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: theme.custom.whiteBackground,
          }}
        >
          <CampaignOutlinedIcon
            sx={{
              width: '22px',
              height: '22px',
              color: theme.palette.primary.red,
            }}
          />
        </Box>
        <Typography
          id='notification-modal-title'
          variant='h5'
          component='h2'
          sx={{
            flexGrow: 1,
            lineHeight: '28px',
            color: theme.custom.tableTextColor,
          }}
        >
          {title || t('main:popupNotification.defaultTitle')}
        </Typography>
        <IconButton
          aria-label={t<string>('main:popupNotification.close')}
          onClick={handleClose}
          sx={{
            flexShrink: 0,
            alignSelf: 'flex-start',
            color: theme.palette.primary.lightGrey,
            ':hover': {
              color: theme.palette.primary.red,
            },
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: theme.spacing(3),
          backgroundColor: theme.custom.whiteBackground,
        }}
      >
        <Typography
          variant='body2'
          sx={{
            // the body comes from the api as plain text, keep its line breaks
            whiteSpace: 'pre-line',
            color: theme.palette.primary.lightGrey,
          }}
        >
          {body}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          padding: theme.spacing(0, 3, 3, 3),
          backgroundColor: theme.custom.whiteBackground,
        }}
      >
        <Button
          variant='actionButton'
          onClick={handleClose}
        >
          {t('main:popupNotification.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
